import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import KnowledgeBackups from '@/components/KnowledgeBackups';
import { Download, Upload, Package, Loader2, CheckCircle2, History, AlertTriangle, FileJson, ShieldCheck, ShieldAlert, Undo2 } from 'lucide-react';

const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const relTime = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (e) { return ''; }
};

// Reusable export/import panel for a portable knowledge package.
// - exportFn:  async () => packageObject   (downloaded as a JSON file)
// - importFn:  async (pkg, modelId, include[]) => { summary, conflicts?, restore_point_id }
// - models:    optional [{model_id, name}] — Explore requires a target model.
// - kind:      'coach' | 'explore' — filters shared history + drives backups.
export default function KnowledgePackagePanel({
  title, description, testIdPrefix, exportFn, importFn, filenameBase, models = null, icon: Icon = Package, kind,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState('');            // 'export' | 'import' | 'undo'
  const [pending, setPending] = useState(null);    // parsed package awaiting confirmation
  const [selected, setSelected] = useState({});    // { typeKey: bool }
  const [modelId, setModelId] = useState('');
  const [integrity, setIntegrity] = useState(null);// { has_checksum, valid }
  const [diff, setDiff] = useState(null);          // { typeKey: {new, update} }
  const [result, setResult] = useState(null);      // { summary, conflicts, restore_point_id }
  const [history, setHistory] = useState([]);
  const [note, setNote] = useState('');

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.getKnowledgePackageHistory(20);
      setHistory((res.items || []).filter((i) => !kind || i.kind === kind).slice(0, 6));
    } catch (e) { /* non-blocking */ }
  }, [kind]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadJson = (pkg, name) => {
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const download = async () => {
    setBusy('export');
    try {
      const pkg = await exportFn(note.trim());
      downloadJson(pkg, `${filenameBase}-${new Date().toISOString().slice(0, 10)}.json`);
      const total = Object.values(pkg.counts || {}).reduce((x, y) => x + y, 0);
      toast.success(`Exported ${total} item${total === 1 ? '' : 's'}`);
      setNote('');
      loadHistory();
    } catch (e) { toast.error(e.message || 'Export failed'); } finally { setBusy(''); }
  };

  const runDiff = useCallback(async (pkg, mId) => {
    try {
      const res = await api.diffKnowledgePackage(pkg, mId || undefined, null);
      setDiff(res.types || {});
    } catch (e) { setDiff(null); }
  }, []);

  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setResult(null); setDiff(null); setIntegrity(null); setModelId('');
    try {
      const pkg = JSON.parse(await file.text());
      if (pkg.package !== 'inzara.knowledge') throw new Error('Not an INZARA knowledge package');
      const counts = pkg.counts || {};
      const sel = {};
      Object.keys(counts).forEach((k) => { sel[k] = (counts[k] || 0) > 0; });
      setSelected(sel);
      setPending(pkg);
      // Integrity check (signing)
      try { setIntegrity(await api.verifyKnowledgePackage(pkg)); } catch (e) { /* ignore */ }
      // Diff dry-run (coach has no model dependency; explore waits for a model)
      if (!models) runDiff(pkg, '');
    } catch (e) {
      toast.error(e.message || 'Could not read package file');
    }
  };

  const onModelChange = (v) => {
    setModelId(v);
    if (v && pending) runDiff(pending, v);
  };

  const chosenKeys = Object.keys(selected).filter((k) => selected[k]);
  const altered = integrity && integrity.has_checksum && integrity.valid === false;
  const canImport = chosenKeys.length > 0 && (!models || !!modelId) && !altered && busy !== 'import';

  const doImport = async () => {
    setBusy('import');
    try {
      const res = await importFn(pending, modelId, chosenKeys);
      setResult({ summary: res.summary || {}, conflicts: res.conflicts || [], restore_point_id: res.restore_point_id });
      setPending(null); setModelId(''); setDiff(null); setIntegrity(null);
      const ins = Object.values(res.summary || {}).reduce((x, y) => x + (y.inserted || 0), 0);
      const upd = Object.values(res.summary || {}).reduce((x, y) => x + (y.updated || 0), 0);
      toast.success(`Imported — ${ins} added, ${upd} updated`);
      loadHistory();
    } catch (e) { toast.error(e.message || 'Import failed'); } finally { setBusy(''); }
  };

  const undo = async () => {
    if (!result?.restore_point_id) return;
    setBusy('undo');
    try {
      const res = await api.rollbackKnowledgeImport(result.restore_point_id);
      toast.success(`Rolled back — ${res.deleted} removed, ${res.restored} restored`);
      setResult(null);
      loadHistory();
    } catch (e) { toast.error(e.message || 'Rollback failed'); } finally { setBusy(''); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5"><Icon className="w-5 h-5 text-primary" /></div>
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for this export (e.g. 'Q3 underwriting terms')"
          maxLength={280}
          className="flex-1 min-w-[220px] text-sm rounded-md border border-border bg-background px-3 py-1.5"
          data-testid={`${testIdPrefix}-note`}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={download} disabled={!!busy} data-testid={`${testIdPrefix}-export`}>
          {busy === 'export' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Export package
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid={`${testIdPrefix}-upload`}>
          <Upload className="w-4 h-4 mr-1.5" />
          Upload &amp; import
        </Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={onFile} data-testid={`${testIdPrefix}-file`} />
      </div>

      {/* Preview + selective import */}
      {pending && (
        <div className="rounded-lg border border-border/60 p-3 space-y-3" data-testid={`${testIdPrefix}-preview`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <FileJson className="w-3.5 h-3.5 text-primary" />
              <span>Package from <span className="text-foreground font-medium">{pending.source_tenant || 'unknown'}</span>{pending.exported_at ? ` · exported ${relTime(pending.exported_at)}` : ''}</span>
            </div>
            {/* Integrity badge (signing) */}
            {integrity && integrity.has_checksum && (
              integrity.valid
                ? <span className="text-[11px] flex items-center gap-1 text-emerald-500" data-testid={`${testIdPrefix}-integrity-ok`}><ShieldCheck className="w-3.5 h-3.5" /> Integrity verified</span>
                : <span className="text-[11px] flex items-center gap-1 text-red-500" data-testid={`${testIdPrefix}-integrity-bad`}><ShieldAlert className="w-3.5 h-3.5" /> Altered — checksum mismatch</span>
            )}
          </div>
          {pending.note && (
            <p className="text-[11px] text-muted-foreground italic border-l-2 border-primary/40 pl-2" data-testid={`${testIdPrefix}-package-note`}>“{pending.note}”</p>
          )}

          <div className="space-y-1.5" data-testid={`${testIdPrefix}-preview-types`}>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Choose what to import</p>
            {Object.keys(pending.counts || {}).map((k) => {
              const count = pending.counts[k] || 0;
              const disabled = count === 0;
              const d = diff && diff[k];
              return (
                <label key={k} className={`flex items-center justify-between gap-3 text-sm ${disabled ? 'opacity-40' : 'cursor-pointer'}`} data-testid={`${testIdPrefix}-type-${k}`}>
                  <span className="flex items-center gap-2">
                    <input type="checkbox" className="accent-primary w-4 h-4" checked={!!selected[k]} disabled={disabled}
                      onChange={(e) => setSelected((s) => ({ ...s, [k]: e.target.checked }))} data-testid={`${testIdPrefix}-check-${k}`} />
                    {humanize(k)}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-2">
                    {d && (
                      <span className="text-[11px]" data-testid={`${testIdPrefix}-diff-${k}`}>
                        <span className="text-emerald-500">{d.new} new</span> · <span className="text-amber-500">{d.update} update</span>
                      </span>
                    )}
                    <span>{count}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {models && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Target semantic model</p>
              <select value={modelId} onChange={(e) => onModelChange(e.target.value)}
                className="text-sm rounded-md border border-border bg-background px-2 py-1.5 min-w-[220px]" data-testid={`${testIdPrefix}-model-select`}>
                <option value="">Select a target model…</option>
                {models.map((m) => (<option key={m.model_id} value={m.model_id}>{m.name || m.display_name || m.model_id}</option>))}
              </select>
            </div>
          )}

          {altered && (
            <p className="text-[11px] text-red-500 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> This package failed its integrity check and can&apos;t be imported.</p>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" disabled={!canImport} onClick={doImport} data-testid={`${testIdPrefix}-confirm-import`}>
              {busy === 'import' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Import {chosenKeys.length} type{chosenKeys.length === 1 ? '' : 's'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setPending(null); setModelId(''); setDiff(null); setIntegrity(null); }} data-testid={`${testIdPrefix}-cancel-import`}>Cancel</Button>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid={`${testIdPrefix}-result`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Import complete</p>
            {result.restore_point_id && (
              <Button size="sm" variant="outline" onClick={undo} disabled={busy === 'undo'} data-testid={`${testIdPrefix}-undo`}>
                {busy === 'undo' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1.5" />}
                Undo this import
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
            {Object.entries(result.summary).map(([k, v]) => (
              <div key={k} className="text-[11px] text-muted-foreground flex justify-between">
                <span>{humanize(k)}</span>
                <span className="text-foreground">+{v.inserted || 0} / ~{v.updated || 0}</span>
              </div>
            ))}
          </div>
          {result.conflicts && result.conflicts.length > 0 && (
            <div className="pt-1.5 border-t border-border/40" data-testid={`${testIdPrefix}-conflicts`}>
              <p className="text-[11px] text-amber-400 flex items-center gap-1 mb-1"><AlertTriangle className="w-3 h-3" /> {result.conflicts.length} glossary term{result.conflicts.length === 1 ? '' : 's'} already existed (updated in place):</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{result.conflicts.slice(0, 30).join(', ')}{result.conflicts.length > 30 ? '…' : ''}</p>
            </div>
          )}
        </div>
      )}

      {/* Package history */}
      {history.length > 0 && (
        <div className="pt-1" data-testid={`${testIdPrefix}-history`}>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1.5"><History className="w-3 h-3" /> Recent activity</p>
          <div className="space-y-1">
            {history.map((h) => {
              const cts = h.action === 'import'
                ? Object.values(h.summary || {}).reduce((x, y) => x + (y.inserted || 0) + (y.updated || 0), 0)
                : Object.values(h.counts || {}).reduce((x, y) => x + y, 0);
              const color = h.action === 'export' ? 'bg-blue-500/15 text-blue-400'
                : h.action === 'rollback' ? 'bg-red-500/15 text-red-400'
                : h.action === 'backup' ? 'bg-purple-500/15 text-purple-400'
                : 'bg-emerald-500/15 text-emerald-400';
              return (
                <div key={h.log_id} className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${color}`}>{h.action}</span>
                    <span>{cts} item{cts === 1 ? '' : 's'}{h.user_email ? ` · ${h.user_email}` : ''}</span>
                  </span>
                  <span>{relTime(h.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {kind && <KnowledgeBackups kind={kind} testIdPrefix={testIdPrefix} />}
    </Card>
  );
}
